<template>
  <div class="delivery-addresses-container">
    <div class="feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="showableErrorMessage" :message="errorMessage" />
      </transition>
    </div>
    <DeliveryAddressNewTitle />
    <DeliveryAddressNewGiftInformation
      :item-name="ticketItemName"
      :item-sku-name="ticketItemSkuName"
      :image-url="ticketImageUrl"
      :brand-name="ticketBrandName"
      :expired-date="ticketExpiredDate"
      :description-html="ticketDescriptionHtml"
      :is-loading-ticket-description-html="isLoadingTicketDescriptionHtml"
    />
    <DeliveryAddressNewForm
      ref="deliveryAddressNewForm"
      :authenticity-token="authenticityToken"
      :prefectures="prefectures"
      :last-name="lastName"
      :first-name="firstName"
      :last-name-kana="lastNameKana"
      :first-name-kana="firstNameKana"
      :zip-code="zipCode"
      :prefecture="prefecture"
      :address1="address1"
      :address2="address2"
      :phone-number="phoneNumber"
      :email="email"
      :is-agreed-privacy-policy="inputtedAgreedPrivacyPolicy"
      :is-over-twenty="inputtedOverTwenty"
      :is-alcohol-item="isAlcoholItem"
      :ticket-token="ticketToken"
      :last-name-error-message="lastNameErrorMessage"
      :first-name-error-message="firstNameErrorMessage"
      :last-name-kana-error-message="lastNameKanaErrorMessage"
      :first-name-kana-error-message="firstNameKanaErrorMessage"
      :zip-code-error-message="zipCodeErrorMessage"
      :prefecture-error-message="prefectureErrorMessage"
      :address1-error-message="address1ErrorMessage"
      :address2-error-message="address2ErrorMessage"
      :phone-number-error-message="phoneNumberErrorMessage"
      :email-error-message="emailErrorMessage"
      :over-twenty-check-error-message="overTwentyCheckErrorMessage"
      :showable-agreed-privacy-policy-error-message="
        showableAgreedPrivacyPolicyErrorMessage
      "
      :is-submittable="isSubmittable"
      :ticket-usable-limit-at="ticketUsableLimitAt"
      @change-last-name="onChangeLastName"
      @change-first-name="onChangeFirstName"
      @change-last-name-kana="onChangeLastNameKana"
      @change-first-name-kana="onChangeFirstNameKana"
      @change-zip-code="onChangeZipCode"
      @change-prefecture="onChangePrefecture"
      @change-address1="onChangeAddress1"
      @change-address2="onChangeAddress2"
      @change-phone-number="onChangePhoneNumber"
      @change-email="onChangeEmail"
      @change-is-agreed-privacy-policy="onChangeIsAgreedPrivacyPolicy"
      @change-is-over-twenty="onChangeIsOverTwenty"
    />
    <Loading v-show="isLoadingPostCode" class="delivery-address__loading" />
  </div>
</template>
<script>
import * as Sentry from '@sentry/browser'
import Postcode from '../../../../api/Postcode'
import GiftCardTicket from '../../../../api/GiftCardTicket'
import Loading from '../../../../components/atoms/loadings/Loading.vue'
import DeliveryAddressNewTitle from '../../../organism/gift_card_tickets/delivery_addresses/input_page/DeliveryAddressNewTitle.vue'
import DeliveryAddressNewGiftInformation from '../../../organism/gift_card_tickets/delivery_addresses/input_page/DeliveryAddressNewGiftInformation.vue'
import DeliveryAddressNewForm from '../../../organism/gift_card_tickets/delivery_addresses/input_page/DeliveryAddressNewForm.vue'
import FeedbackError from '../../../atoms/feedbacks/feedback-error.vue'

const ZIPCODE_REGEXP = /[0-9]{7}/
const NUM_REGEXP = /[0-9]/
const isNotNumberString = argument => !NUM_REGEXP.test(argument)

export default {
  components: {
    FeedbackError,
    DeliveryAddressNewForm,
    DeliveryAddressNewGiftInformation,
    DeliveryAddressNewTitle,
    Loading
  },
  props: {
    shouldShowError: {
      type: Boolean,
      required: true
    },
    ticketImageUrl: {
      type: String,
      required: true
    },
    ticketItemName: {
      type: String,
      required: true
    },
    ticketItemSkuName: {
      type: String,
      required: true
    },
    ticketBrandName: {
      type: String,
      required: true
    },
    ticketExpiredDate: {
      type: String,
      required: true
    },
    authenticityToken: {
      type: String,
      required: true
    },
    prefecturesObject: {
      type: Object,
      required: true
    },
    ticketToken: {
      type: String,
      required: true
    },
    isAlcoholItem: {
      type: Boolean,
      required: true
    },
    inputtedLastName: {
      type: String,
      required: true
    },
    inputtedFirstName: {
      type: String,
      required: true
    },
    inputtedLastNameKana: {
      type: String,
      required: true
    },
    inputtedFirstNameKana: {
      type: String,
      required: true
    },
    inputtedZipCode: {
      type: String,
      required: true
    },
    inputtedPrefecture: {
      type: String,
      required: true
    },
    inputtedAddress1: {
      type: String,
      required: true
    },
    inputtedAddress2: {
      type: String,
      required: true
    },
    inputtedPhoneNumber: {
      type: String,
      required: true
    },
    inputtedEmail: {
      type: String,
      required: true
    },
    inputtedAgreedPrivacyPolicy: {
      type: Boolean,
      required: true
    },
    inputtedOverTwenty: {
      type: Boolean,
      required: true
    },
    lastNameErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    firstNameErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    lastNameKanaErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    firstNameKanaErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    zipCodeErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    prefectureErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    address1ErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    address2ErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    phoneNumberErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    emailErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    overTwentyCheckErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    showableAgreedPrivacyPolicyErrorMessage: {
      type: Boolean,
      required: true
    },
    ticketUsableLimitAt: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showableErrorMessage: false,
      errorMessage: '',
      threeSteps: ['入力', '確認', '完了'],
      lastName: '',
      firstName: '',
      lastNameKana: '',
      firstNameKana: '',
      zipCode: '',
      prefecture: '',
      address1: '',
      address2: '',
      phoneNumber: '',
      email: '',
      isOverTwenty: false,
      isAgreedPrivacyPolicy: false,
      isLoadingPostCode: false,
      ticketDescriptionHtml: '',
      isLoadingTicketDescriptionHtml: false
    }
  },
  computed: {
    isLastNameInputted() {
      return this.lastName.length !== 0
    },
    isFirstNameInputted() {
      return this.firstName.length !== 0
    },
    isLastNameKanaInputted() {
      return this.lastNameKana.length !== 0
    },
    isFirstNameKanaInputted() {
      return this.firstNameKana.length !== 0
    },
    isZipCodeInputted() {
      return this.zipCode.length !== 0
    },
    isAddress1Inputted() {
      return this.address1.length !== 0
    },
    isAddress2Inputted() {
      return this.address2.length !== 0
    },
    isPhoneNumberInputted() {
      return this.phoneNumber.length !== 0
    },
    isEmailInputted() {
      return this.email.length !== 0
    },
    isPrefectureSelected() {
      return this.prefecture !== ''
    },
    isOverTwentyChecked() {
      return !this.isAlcoholItem || this.isOverTwenty
    },
    isSubmittable() {
      return (
        this.isLastNameInputted &&
        this.isFirstNameInputted &&
        this.isLastNameKanaInputted &&
        this.isFirstNameKanaInputted &&
        this.isZipCodeInputted &&
        this.isAddress1Inputted &&
        this.isAddress2Inputted &&
        this.isPhoneNumberInputted &&
        this.isEmailInputted &&
        this.isPrefectureSelected &&
        this.isOverTwentyChecked &&
        this.isAgreedPrivacyPolicy
      )
    },
    hasNotFinishedEnteringZipCode() {
      return !ZIPCODE_REGEXP.test(this.zipCode)
    },
    prefectures() {
      return this.prefecturesObject.data.map(v => v.attributes)
    }
  },
  async mounted() {
    this.initializeInputtedValues()
    this.isLoadingTicketDescriptionHtml = true
    try {
      this.ticketDescriptionHtml = await GiftCardTicket.getAddressInputDescriptionHtml(
        this.ticketToken
      )
    } catch (error) {
      Sentry.captureException(error)
      this.errorMessage =
        'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      this.showableErrorMessage = true
      return
    }
    this.isLoadingTicketDescriptionHtml = false
    this.errorMessage = '入力内容にエラーがあります'
    this.showableErrorMessage = this.shouldShowError
  },
  methods: {
    initializeInputtedValues() {
      this.lastName = this.inputtedLastName
      this.firstName = this.inputtedFirstName
      this.lastNameKana = this.inputtedLastNameKana
      this.firstNameKana = this.inputtedFirstNameKana
      this.zipCode = this.inputtedZipCode
      this.prefecture = this.inputtedPrefecture
      this.address1 = this.inputtedAddress1
      this.address2 = this.inputtedAddress2
      this.phoneNumber = this.inputtedPhoneNumber
      this.email = this.inputtedEmail
      this.isOverTwenty = this.inputtedOverTwenty
      this.isAgreedPrivacyPolicy = this.inputtedAgreedPrivacyPolicy
    },
    onClickOverTwenty() {
      this.isOverTwenty = !this.isOverTwenty
    },
    onAgreedPrivacyPolicyChecked() {
      this.isAgreedPrivacyPolicy = !this.isAgreedPrivacyPolicy
    },
    onClickPrivacyPolicyLink() {
      event.stopPropagation()
    },
    async autoCompletionAddressByZipCode() {
      this.isLoadingPostCode = true
      setTimeout(async () => {
        try {
          const addresses = await Postcode.getAddressesFromZipCode(
            this.zipCode,
            {
              fields: 'pref,city,town'
            }
          )
          if (addresses.length === 0) {
            this.isLoadingPostCode = false
            return
          }
          this.prefecture = this.prefectures
            .find(pref => pref.name === addresses[0].pref)
            .id.toString()
          this.address1 = addresses[0].city + addresses[0].town
          this.$refs.deliveryAddressNewForm.focusAddress2()
        } catch (error) {
          // NOTE: 郵便番号検索が失敗した場合でも正しく動作させるためにSentryに通知だけしてスルーさせる
          Sentry.captureException(error)
          console.log(error)
        }
        this.isLoadingPostCode = false
      }, 1000)
    },
    enterError() {
      setTimeout(() => {
        this.showableErrorMessage = false
      }, 3000)
    },
    onChangeLastName(value) {
      this.lastName = value
    },
    onChangeFirstName(value) {
      this.firstName = value
    },
    onChangeLastNameKana(value) {
      this.lastNameKana = value
    },
    onChangeFirstNameKana(value) {
      this.firstNameKana = value
    },
    async onChangeZipCode(value) {
      this.zipCode = value
      if (
        this.hasNotFinishedEnteringZipCode ||
        isNotNumberString(this.zipCode) ||
        this.isLoadingPostCode
      ) {
        return
      }
      await this.autoCompletionAddressByZipCode()
    },
    onChangePrefecture(value) {
      this.prefecture = value
    },
    onChangeAddress1(value) {
      this.address1 = value
    },
    onChangeAddress2(value) {
      this.address2 = value
    },
    onChangePhoneNumber(value) {
      this.phoneNumber = value
    },
    onChangeEmail(value) {
      this.email = value
    },
    onChangeIsAgreedPrivacyPolicy(value) {
      this.isAgreedPrivacyPolicy = value
    },
    onChangeIsOverTwenty(value) {
      this.isOverTwenty = value
    }
  }
}
</script>
